import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import type { Me, Tenant } from './types';
import { Leads } from './views/Leads';
import { OptOutList } from './views/OptOutList';
import { FunnelEditor } from './views/FunnelEditor';
import { KnowledgeCenter } from './views/KnowledgeCenter';
import { UserManagement } from './views/UserManagement';
import { ConnectWhatsApp } from './views/ConnectWhatsApp';

type Tab = 'leads' | 'optout' | 'funnel' | 'knowledge' | 'whatsapp' | 'users';

/** Minimal inline icons for the sidebar (stroke-based, inherit currentColor). */
function NavIcon({ name }: { name: Tab }) {
  const common = {
    className: 'nav-icon',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'leads':
      return (
        <svg {...common}>
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <circle cx="9" cy="7" r="4" />
          <path d="M2 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" />
        </svg>
      );
    case 'optout':
      return (
        <svg {...common}>
          <path d="M18.36 6.64A9 9 0 1 1 5.64 6.64" />
          <line x1="12" y1="2" x2="12" y2="12" />
        </svg>
      );
    case 'funnel':
      return (
        <svg {...common}>
          <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" />
        </svg>
      );
    case 'knowledge':
      return (
        <svg {...common}>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      );
    case 'whatsapp':
      return (
        <svg {...common}>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      );
    case 'users':
      return (
        <svg {...common}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
  }
}

export default function App({ signOut }: { signOut?: () => void }) {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('leads');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  // SystemAdmin can switch which agency they're viewing; others are locked.
  const [activeTenantId, setActiveTenantId] = useState<string | undefined>(undefined);

  useEffect(() => {
    api.me().then(setMe).catch((e) => setError(String(e)));
  }, []);

  const isSystemAdmin = me?.role === 'SystemAdmin';
  const isAgencyAdmin = me?.role === 'AgencyAdmin' || isSystemAdmin;

  useEffect(() => {
    if (isSystemAdmin) {
      api
        .listTenants()
        .then((t) => {
          setTenants(t);
          setActiveTenantId((cur) => cur ?? t[0]?.tenant_id);
        })
        .catch(() => undefined);
    }
  }, [isSystemAdmin]);

  const scope = isSystemAdmin ? activeTenantId : undefined;

  const tabs = useMemo(() => {
    const list: { id: Tab; label: string }[] = [
      { id: 'leads', label: 'לידים' },
      { id: 'optout', label: 'רשימת הסרה' },
    ];
    if (isAgencyAdmin) {
      list.push({ id: 'funnel', label: 'עריכת בוט' });
      list.push({ id: 'knowledge', label: 'מאגר ידע' });
      list.push({ id: 'whatsapp', label: 'חיבור וואטסאפ' });
      list.push({ id: 'users', label: isSystemAdmin ? 'סוכנויות ומשתמשים' : 'ניהול משתמשים' });
    }
    return list;
  }, [isAgencyAdmin, isSystemAdmin]);

  if (error) return <div className="empty">שגיאה: {error}</div>;
  if (!me) return <div className="empty">טוען…</div>;

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <img src="/SmartBot_Logo-Final.png" alt="SmartBot" />
          <div>
            <div className="brand-name">SmartBot</div>
            <div className="brand-sub">מערכת CRM חכמה</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-group-label">תפריט ראשי</div>
          {tabs.map((t) => (
            <button
              key={t.id}
              className={`nav-item ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <NavIcon name={t.id} />
              <span>{t.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-line">{me.email}</div>
          <div className="user-role">{me.role}</div>
          <button className="btn secondary" onClick={() => signOut?.()}>
            התנתקות
          </button>
        </div>
      </aside>

      <div className="content">
        {isSystemAdmin && tenants.length > 0 && tab !== 'users' && (
          <div className="content-topbar">
            <span className="badge">סוכנות פעילה</span>
            <select
              className="tenant-switcher"
              value={activeTenantId}
              onChange={(e) => setActiveTenantId(e.target.value)}
            >
              {tenants.map((t) => (
                <option key={t.tenant_id} value={t.tenant_id}>
                  {t.name} ({t.tenant_id})
                </option>
              ))}
            </select>
            <div className="spacer" />
          </div>
        )}

        <div className="body">
          {tab === 'leads' && <Leads tenantId={scope} role={me.role} email={me.email} />}
          {tab === 'optout' && <OptOutList tenantId={scope} role={me.role} />}
          {tab === 'funnel' && isAgencyAdmin && <FunnelEditor tenantId={scope} />}
          {tab === 'knowledge' && isAgencyAdmin && <KnowledgeCenter tenantId={scope} />}
          {tab === 'whatsapp' && isAgencyAdmin && <ConnectWhatsApp tenantId={scope} />}
          {tab === 'users' && isAgencyAdmin && <UserManagement role={me.role} tenantId={me.tenantId} />}
        </div>
      </div>
    </div>
  );
}
